import { useEffect, useRef, useState, useMemo } from "react";
import "./DataFlowVisualization.css";
import SampleCard from "./SampleCard";

interface DataFlowVisualizationProps {
  data: any[]; // Array of data items
  maxVisible?: number; // Maximum number of visible rows (default: 10)
  maxDataPoints?: number; // Maximum number of data points to keep in memory (default: 100)
  height?: number; // Container height (default: 400)
  width?: number; // Container width (default: 1300)
  rowWidth?: number; // Width of each table row card (default: 280)
  onItemClick?: (item: any) => void; // Callback when row is clicked
  getValue?: (item: any) => number; // Function to extract value (kept for compatibility)
  color?: string; // Single color for all rows (default: "#2196F3")
}

/**
 * Data Flow Visualization Component using HTML Table
 * Displays data as horizontal table rows that stream from left to right
 * - New rows enter from the left
 * - Existing rows shift right when new ones arrive
 * - Maximum visible rows controlled by maxVisible prop
 * - Hover pauses animation
 * - Click opens modal (via onItemClick callback)
 */
export default function DataFlowVisualization({
  data,
  maxVisible: _maxVisible = 10, // Kept for backward compatibility but not used (showing all data for streaming)
  maxDataPoints = 100, // Maximum number of data points to keep in memory
  height = 400,
  width = 1080,
  rowWidth = 280,
  onItemClick,
  getValue: _getValue, // Kept for backward compatibility but not used in table view
  color = "#2196F3", // Single color for all rows
}: DataFlowVisualizationProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const rowsWrapperRef = useRef<HTMLDivElement>(null);
  const prevDataLengthRef = useRef<number>(0);
  const [isPaused, setIsPaused] = useState(false);
  const [shiftAmount, setShiftAmount] = useState(0);
  const gap = 20; // Gap between rows
  const transitionDuration = 800; // ms

  // Automatically limit data to maxDataPoints by keeping only the most recent items
  // When data exceeds maxDataPoints, the oldest items are automatically removed
  // This ensures memory efficiency and focuses on the latest streaming data
  const visibleData = useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }
    if (data.length <= maxDataPoints) {
      return data;
    }
    // Keep only the most recent maxDataPoints items (remove oldest ones from the beginning)
    // slice(-maxDataPoints) takes the last N items, effectively removing the oldest
    return data.slice(-maxDataPoints);
  }, [data, maxDataPoints]);

  // Extract key fields from data item for display
  const getDisplayFields = (item: any) => {
    const fields: { label: string; value: string }[] = [];

    // Common fields
    if (item.timestamp !== undefined) {
      fields.push({
        label: "Time",
        value: new Date(item.timestamp).toLocaleTimeString(),
      });
    }
    if (item.marketIndex !== undefined) {
      fields.push({ label: "Market", value: String(item.marketIndex) });
    }
    if (item.symbol !== undefined) {
      fields.push({ label: "Symbol", value: String(item.symbol) });
    }

    // Kline specific
    if (item.open !== undefined) {
      const numValue = Number(item.open);
      fields.push({
        label: "Open",
        value: !isNaN(numValue) ? numValue.toFixed(4) : String(item.open),
      });
    }
    if (item.close !== undefined) {
      const numValue = Number(item.close);
      fields.push({
        label: "Close",
        value: !isNaN(numValue) ? numValue.toFixed(4) : String(item.close),
      });
    }
    if (item.high !== undefined) {
      const numValue = Number(item.high);
      fields.push({
        label: "High",
        value: !isNaN(numValue) ? numValue.toFixed(4) : String(item.high),
      });
    }
    if (item.low !== undefined) {
      const numValue = Number(item.low);
      fields.push({
        label: "Low",
        value: !isNaN(numValue) ? numValue.toFixed(4) : String(item.low),
      });
    }
    if (item.volume !== undefined) {
      const numValue = Number(item.volume);
      fields.push({
        label: "Volume",
        value: !isNaN(numValue) ? numValue.toFixed(2) : String(item.volume),
      });
    }

    // Price specific
    if (item.price !== undefined) {
      const numValue = Number(item.price);
      fields.push({
        label: "Price",
        value: !isNaN(numValue) ? numValue.toFixed(4) : String(item.price),
      });
    }
    if (item.mark_price !== undefined) {
      const numValue = Number(item.mark_price);
      fields.push({
        label: "Mark Price",
        value: !isNaN(numValue) ? numValue.toFixed(4) : String(item.mark_price),
      });
    }
    if (item.last_price !== undefined) {
      const numValue = Number(item.last_price);
      fields.push({
        label: "Last Price",
        value: !isNaN(numValue) ? numValue.toFixed(4) : String(item.last_price),
      });
    }

    // Position specific
    if (item.size !== undefined) {
      const numValue = Number(item.size);
      fields.push({
        label: "Size",
        value: !isNaN(numValue) ? numValue.toFixed(4) : String(item.size),
      });
    }
    if (item.entryPrice !== undefined) {
      const numValue = Number(item.entryPrice);
      fields.push({
        label: "Entry",
        value: !isNaN(numValue) ? numValue.toFixed(4) : String(item.entryPrice),
      });
    }
    if (item.unrealizedPnL !== undefined) {
      const numValue = Number(item.unrealizedPnL);
      fields.push({
        label: "PnL",
        value: !isNaN(numValue)
          ? numValue.toFixed(4)
          : String(item.unrealizedPnL),
      });
    }

    // Order specific
    if (item.orderId !== undefined) {
      fields.push({ label: "Order ID", value: String(item.orderId) });
    }
    if (item.status !== undefined) {
      fields.push({ label: "Status", value: String(item.status) });
    }

    // Bucket specific
    if (item.bucketId !== undefined) {
      fields.push({ label: "Bucket ID", value: String(item.bucketId) });
    }
    if (item.maxLeverage !== undefined) {
      fields.push({ label: "Max Lev", value: String(item.maxLeverage) });
    }

    // Generic value field
    if (item.value !== undefined && fields.length === 0) {
      const numValue = Number(item.value);
      fields.push({
        label: "Value",
        value: !isNaN(numValue) ? numValue.toFixed(4) : String(item.value),
      });
    }

    // If no fields found, show a summary
    if (fields.length === 0) {
      fields.push({ label: "Data", value: "Click to view" });
    }

    return fields.slice(0, 6); // Limit to 6 fields for display
  };

  // Update shift amount when data changes - always scroll to show newest (rightmost) items
  useEffect(() => {
    if (!rowsWrapperRef.current || !containerRef.current) return;

    const totalRows = visibleData.length;
    if (totalRows === 0) {
      setShiftAmount(0);
      prevDataLengthRef.current = 0;
      return;
    }

    const containerWidth = containerRef.current.offsetWidth;
    const padding = 20; // Container padding
    const availableWidth = containerWidth - padding * 2;

    // Calculate total width needed for all rows
    const totalContentWidth = totalRows * rowWidth + (totalRows - 1) * gap;

    // Always scroll to show the newest (rightmost) items
    // If content is wider than container, shift left to show rightmost items
    if (totalContentWidth > availableWidth) {
      // Shift left by the difference to show rightmost items
      const shift = totalContentWidth - availableWidth;
      setShiftAmount(-shift);
    } else {
      // All items fit, no shift needed
      setShiftAmount(0);
    }

    // Track visible data length to detect new items being displayed
    // Use visibleData.length to accurately track what's actually shown
    const hasNewData = visibleData.length > prevDataLengthRef.current;
    prevDataLengthRef.current = visibleData.length;

    // When new data arrives, ensure smooth scroll to end
    if (hasNewData && rowsWrapperRef.current) {
      // Use requestAnimationFrame to ensure DOM is updated before applying transform
      requestAnimationFrame(() => {
        if (rowsWrapperRef.current) {
          // Force reflow to trigger smooth transition
          void rowsWrapperRef.current.offsetWidth;
        }
      });
    }
  }, [visibleData.length, rowWidth, gap, width]);

  // Apply CSS transform for smooth shifting
  useEffect(() => {
    if (!rowsWrapperRef.current) return;

    rowsWrapperRef.current.style.transition = isPaused
      ? "none"
      : `transform ${transitionDuration}ms ease-out`;
    rowsWrapperRef.current.style.transform = `translateX(${shiftAmount}px)`;
  }, [shiftAmount, isPaused, transitionDuration]);

  return (
    <div
      ref={containerRef}
      className="data-flow-container"
      style={{
        width: `${width}px`,
        height: `${height}px`,
      }}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Horizontal scrolling container */}
      <div
        ref={rowsWrapperRef}
        className="data-flow-rows-wrapper"
        style={{
          gap: `${gap}px`,
        }}
      >
        {visibleData.map((item, index) => {
          const displayFields = getDisplayFields(item);
          const originalIndex = index; // Since visibleData is now all data, index is the original index

          return (
            <SampleCard
              key={`${originalIndex}-${item.timestamp || Date.now()}-${item.id || JSON.stringify(item)}`}
              item={item}
              displayFields={displayFields}
              originalIndex={originalIndex}
              rowWidth={rowWidth}
              color={color}
              onItemClick={onItemClick}
            />
          );
        })}
      </div>

      {/* Paused indicator */}
      {isPaused && <div className="data-flow-paused-indicator">Paused</div>}
    </div>
  );
}
